import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";

export default function Login() {
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(mobile.trim(), password);
      toast.success("Login successful / लॉगिन सफल");
      navigate(`/${user.role}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 bg-brand-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="h-10 aspect-square rounded-md bg-white flex items-center justify-center text-[#1a3ba8] font-display font-bold text-lg">E</div>
            <span className="font-display text-2xl font-bold tracking-tight">
              <span>EPAY</span>
              <span className="text-[#f57320]">CASH</span>
            </span>
          </Link>
        </div>
        <div className="relative">
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Welcome back / <span className="text-[#f57320]">वापसी पर स्वागत है</span></h2>
          <p className="mt-4 text-white/80 max-w-md">Login कीजिए और अपने role के हिसाब से dashboard access करें — Admin, Vendor, Employee या Merchant.</p>
          <ul className="mt-8 space-y-3 text-sm text-white/90">
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#f57320]" /> 100% secure JWT authentication</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#f57320]" /> Mobile + Password login</li>
            <li className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#f57320]" /> Instant wallet & lead tracking</li>
          </ul>
        </div>
        <div className="relative text-xs text-white/70">© {new Date().getFullYear()} EPAYCASH</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-white">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-6"><Logo /></div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Sign in</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Login to your account</h1>
          <p className="text-sm text-slate-500 mt-2">Mobile और password के साथ login कीजिए</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="mobile">Mobile / मोबाइल</Label>
              <Input
                id="mobile"
                data-testid="login-mobile-input"
                inputMode="numeric"
                placeholder="10-digit mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password / पासवर्ड</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-brand-gradient hover:opacity-90 text-white rounded-md shadow-md shadow-[#1a3ba8]/25"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Login / लॉगिन"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600 flex justify-between">
            <Link to="/" className="hover:text-[#1a3ba8]">← Back to home</Link>
            <span>
              New merchant?{" "}
              <Link to="/register" className="text-[#f57320] hover:underline font-medium">Register</Link>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
